import { Button } from '@/common/ui/Form/Button'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Input } from '@/common/ui/Form/Input'
import { Select, SelectOption } from '@/common/ui/Form/Select'
import { Icon } from '@/common/ui/Icon'
import { cn } from '@/common/lib/cn/cn'
import { Modal } from '@/common/ui/Modal'
import { useIntegrationsForm, useMarketplaceOauthIntegration } from '../model'
import { MARKETPLACE_OAUTH_MODAL_ID } from '../lib/marketplaceOauth'
import { useCurrentOrg } from '../../org/model'
import type { MarketplaceType } from '@/common/api/client/types.gen'
import type { GetIntegrationsResponse } from '@/common/types/api'
import { useEffect, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'

export const IntegrationsForm = ({
    className,
    editingIntegration,
    existingIntegrations = [],
    onBack,
    backLabel = 'Отмена',
    modalId = 'integrationModal',
}: {
    className?: string
    editingIntegration?: GetIntegrationsResponse[0]
    existingIntegrations?: GetIntegrationsResponse
    onBack?: () => void
    backLabel?: string
    modalId?: string
}) => {
    const {
        form,
        handleSubmit,
        onSubmit,
        register,
        isPending,
        isError,
        isSuccess,
        error,
        shouldShowClientId,
        isEditMode,
    } = useIntegrationsForm(editingIntegration)

    const marketplace = useWatch({ control: form.control, name: 'marketplace' })
    const isWildberriesMarketplace = marketplace === 'wildberries' || editingIntegration?.marketplace === 'wildberries'
    const isOzonMarketplace = marketplace === 'ozon' || editingIntegration?.marketplace === 'ozon'

    const { data: currentOrg } = useCurrentOrg()
    const showOzonOauth = isOzonMarketplace
    const {
        oauthModalPhase,
        oauthErrorMessage,
        isOauthLoading,
        successMessage,
        handleMarketplaceOauthClick,
        handleCancelMarketplaceOauth,
        handleCloseOauthResultModal,
    } = useMarketplaceOauthIntegration({
        orgId: currentOrg?.id,
        integrationId: editingIntegration?.id,
        modalId,
        enabled: showOzonOauth && Boolean(currentOrg?.id) && (!isEditMode || Boolean(editingIntegration?.id)),
        isEditMode,
        onSuccess: () => form.reset(),
    })
    const [isWbTokenHelpOpen, setIsWbTokenHelpOpen] = useState(false)
    const [isOzonTokenHelpOpen, setIsOzonTokenHelpOpen] = useState(false)

    // Получаем список уже используемых маркетплейсов
    const usedMarketplaces = existingIntegrations.map(integration => integration.marketplace)

    type MarketplaceOption = { value: MarketplaceType; label: string }

    const marketplaceOptionsBase: MarketplaceOption[] = [
        { value: 'wildberries', label: 'Wildberries' },
        { value: 'ozon', label: 'Ozon' },
        // { value: 'yandex_market', label: 'Yandex Market' },
        // { value: 'avito', label: 'Avito' },
        // { value: 'megamarket', label: 'Megamarket' },
    ]

    // Фильтруем доступные маркетплейсы
    const availableMarketplaces = marketplaceOptionsBase.filter(marketplace => {
        if (editingIntegration && marketplace.value === editingIntegration.marketplace) {
            return true
        }
        return !usedMarketplaces.includes(marketplace.value)
    })

    useEffect(() => {
        if (isSuccess) {
            form.reset()
            const checkbox = document.getElementById(modalId) as HTMLInputElement

            if (checkbox) {
                checkbox.checked = false
            }
        }
    }, [isSuccess, form, modalId])

    useEffect(() => {
        if (!isEditMode) {
            form.setValue('marketplace', '' as MarketplaceType, { shouldValidate: false })
        }
    }, [form, isEditMode])

    useEffect(() => {
        if (!isWildberriesMarketplace) {
            setIsWbTokenHelpOpen(false)
        }
    }, [isWildberriesMarketplace])

    useEffect(() => {
        if (!isOzonMarketplace) {
            setIsOzonTokenHelpOpen(false)
        }
    }, [isOzonMarketplace])

    const handleToggleWbTokenHelp = () => {
        setIsWbTokenHelpOpen(prev => !prev)
    }

    const handleToggleOzonTokenHelp = () => {
        setIsOzonTokenHelpOpen(prev => !prev)
    }

    if (availableMarketplaces.length === 0 && !isEditMode) {
        return (
            <div className={cn('modal-form', className)}>
                <div className='text-center py-8'>
                    <p className='text-base-content/70 mb-4'>Все доступные маркетплейсы уже добавлены</p>
                    <Button as='label' htmlFor='integrationModal' emphasis='secondary' variant='ghost' size='lg'>
                        Закрыть
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <form className={cn('modal-form', className)} onSubmit={handleSubmit(onSubmit)}>
            <div className='modal-form-fields'>
                <div className='modal-form-field'>
                    <Fieldset errorText={form.formState.errors.marketplace?.message} required>
                        <Controller
                            name='marketplace'
                            control={form.control}
                            disabled={isEditMode}
                            rules={{ required: 'Введите название маркетплейса' }}
                            render={({ field }) => (
                                <Select
                                    disabled={isEditMode}
                                    className='w-full'
                                    size='lg'
                                    placeholder='Выберите маркетплейс'
                                    {...field}
                                >
                                    {availableMarketplaces.map(marketplace => (
                                        <SelectOption key={marketplace.value} value={marketplace.value}>
                                            {marketplace.label}
                                        </SelectOption>
                                    ))}
                                </Select>
                            )}
                        />
                    </Fieldset>
                </div>
                {showOzonOauth ? (
                    <div className='modal-form-field'>
                        <Button
                            type='button'
                            variant='outline'
                            emphasis='secondary'
                            size='xl'
                            className='w-full gap-2'
                            disabled={isOauthLoading}
                            loading={isOauthLoading}
                            aria-label={
                                isEditMode
                                    ? 'Переподключить с помощью Ozon Seller Id'
                                    : 'Добавить с помощью Ozon Seller Id'
                            }
                            onClick={handleMarketplaceOauthClick}
                        >
                            <Icon name='ozon' size='md' aria-hidden />
                            {isEditMode
                                ? 'Переподключить с помощью Ozon Seller Id'
                                : 'Добавить с помощью Ozon Seller Id'}
                        </Button>
                        <div className='mt-4 flex items-center gap-3' aria-hidden>
                            <span className='h-px flex-1 bg-base-300' />
                            <span className='text-sm text-base-content/60'>или</span>
                            <span className='h-px flex-1 bg-base-300' />
                        </div>
                    </div>
                ) : null}
                {isWildberriesMarketplace ? (
                    <div className='modal-form-field'>
                        <div className='overflow-hidden rounded-[8px] border border-[var(--pixorion-table-grey)] bg-base-200/25'>
                            <Button
                                type='button'
                                variant='ghost'
                                emphasis='secondary'
                                size='lg'
                                className='w-full justify-between gap-3 rounded-none px-4 text-left font-medium normal-case'
                                aria-expanded={isWbTokenHelpOpen}
                                aria-controls='integration-wb-token-help'
                                id='integration-wb-token-help-trigger'
                                onClick={handleToggleWbTokenHelp}
                            >
                                <span className='flex min-w-0 items-center gap-2.5'>Как получить токен</span>
                                <Icon
                                    name='arrow-down'
                                    size='sm'
                                    className={cn(
                                        'shrink-0 transition-transform duration-200',
                                        isWbTokenHelpOpen && 'rotate-180'
                                    )}
                                    aria-hidden
                                />
                            </Button>
                            {isWbTokenHelpOpen ? (
                                <div
                                    id='integration-wb-token-help'
                                    role='region'
                                    aria-labelledby='integration-wb-token-help-trigger'
                                    className='space-y-3 border-t border-[var(--pixorion-table-grey)] px-4 pb-4 pt-3'
                                >
                                    <p className='text-sm leading-relaxed text-base-content/80'>
                                        Получите API-токен в личном кабинете продавца на{' '}
                                        <a
                                            href='https://seller.wildberries.ru'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent'
                                        >
                                            seller.wildberries.ru
                                        </a>
                                        :
                                    </p>
                                    <ol className='list-decimal space-y-2.5 pl-5 text-sm leading-relaxed text-base-content/90 marker:font-semibold marker:text-base-content'>
                                        <li>Войдите под учётной записью владельца.</li>
                                        <li>Откройте настройки: нажмите на имя профиля справа сверху → «Настройки».</li>
                                        <li>Перейдите во вкладку «Интеграции по API» или «Доступ к API».</li>
                                        <li>Нажмите «Создать новый токен».</li>
                                        <li>
                                            Задайте название (например, pixorion) и отметьте категории: контент,
                                            статистика, цены и скидки, маркетплейс. Нажмите «Создать».
                                        </li>
                                        <li>Скопируйте токен в эту форму.</li>
                                    </ol>
                                    <div
                                        className='rounded-xl border border-warning/35 bg-warning/10 px-3 py-2.5 text-sm leading-relaxed text-warning'
                                        role='status'
                                    >
                                        <span className='font-semibold'>Важно:</span> токен показывается только один
                                        раз. Если закрыть окно без копирования, восстановить его нельзя — только
                                        перевыпуск.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {isOzonMarketplace ? (
                    <div className='modal-form-field'>
                        <div className='overflow-hidden rounded-[8px] border border-[var(--pixorion-table-grey)] bg-base-200/25'>
                            <Button
                                type='button'
                                variant='ghost'
                                emphasis='secondary'
                                size='lg'
                                className='w-full justify-between gap-3 rounded-none px-4 text-left font-medium normal-case'
                                aria-expanded={isOzonTokenHelpOpen}
                                aria-controls='integration-ozon-token-help'
                                id='integration-ozon-token-help-trigger'
                                onClick={handleToggleOzonTokenHelp}
                            >
                                <span className='flex min-w-0 items-center gap-2.5'>Как получить токен</span>
                                <Icon
                                    name='arrow-down'
                                    size='sm'
                                    className={cn(
                                        'shrink-0 transition-transform duration-200',
                                        isOzonTokenHelpOpen && 'rotate-180'
                                    )}
                                    aria-hidden
                                />
                            </Button>
                            {isOzonTokenHelpOpen ? (
                                <div
                                    id='integration-ozon-token-help'
                                    role='region'
                                    aria-labelledby='integration-ozon-token-help-trigger'
                                    className='space-y-3 border-t border-[var(--pixorion-table-grey)] px-4 pb-4 pt-3'
                                >
                                    <p className='text-sm leading-relaxed text-base-content/80'>
                                        Чтобы получить API-токен для Ozon Seller, войдите в личный кабинет на{' '}
                                        <a
                                            href='https://seller.ozon.ru'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent'
                                        >
                                            seller.ozon.ru
                                        </a>
                                        , откройте «Настройки» → «API ключи» (или «Seller API»), нажмите «Сгенерировать
                                        ключ» и скопируйте токен. Client ID также находится в этом разделе.
                                    </p>
                                    <ol className='list-decimal space-y-2.5 pl-5 text-sm leading-relaxed text-base-content/90 marker:font-semibold marker:text-base-content'>
                                        <li>Войдите в личный кабинет на seller.ozon.ru.</li>
                                        <li>Откройте настройки: нажмите на имя профиля справа сверху → «Настройки».</li>
                                        <li>Перейдите во вкладку «API ключи» или «Seller API».</li>
                                        <li>Нажмите «Сгенерировать ключ».</li>
                                        <li>
                                            Укажите название ключа, выберите цель, сгенерируйте ключ и скопируйте токен
                                            в эту форму.
                                        </li>
                                        <li>
                                            Скопируйте Client ID из того же раздела — он нужен для внешних интеграций.
                                        </li>
                                    </ol>
                                    <div
                                        className='rounded-xl border border-warning/35 bg-warning/10 px-3 py-2.5 text-sm leading-relaxed text-warning'
                                        role='status'
                                    >
                                        <span className='font-semibold'>Важно:</span> токен показывается только один
                                        раз. Если закрыть окно без копирования, восстановить его нельзя — только
                                        перевыпуск.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {shouldShowClientId && (
                    <div className='modal-form-field'>
                        <Fieldset errorText={form.formState.errors.client_id?.message} required>
                            <Input
                                emphasis='neutral'
                                className='w-full'
                                size='lg'
                                placeholder='Введите Client ID'
                                type='text'
                                autoComplete='off'
                                spellCheck='false'
                                {...register('client_id', {
                                    required: shouldShowClientId ? 'Client ID обязателен' : false,
                                })}
                            />
                        </Fieldset>
                    </div>
                )}
                <div className='modal-form-field'>
                    <Fieldset errorText={form.formState.errors.api_token?.message} required>
                        <Input
                            emphasis='neutral'
                            className='w-full'
                            size='lg'
                            placeholder='Введите API-токен'
                            type='text'
                            autoComplete='off'
                            spellCheck='false'
                            {...register('api_token', {
                                required: 'API-токен обязателен',
                            })}
                        />
                    </Fieldset>
                </div>

                {isError && <div className='text-error text-sm text-center'>{error?.message}</div>}
            </div>
            <div className='modal-form-actions'>
                <Button
                    className='w-full'
                    emphasis='accent'
                    size='xl'
                    type='submit'
                    disabled={isPending}
                    loading={isPending}
                >
                    {isEditMode ? 'Сохранить' : 'Добавить'}
                </Button>
                {onBack ? (
                    <Button
                        type='button'
                        className='w-full'
                        emphasis='secondary'
                        size='lg'
                        variant='ghost'
                        onClick={onBack}
                    >
                        {backLabel}
                    </Button>
                ) : (
                    <Button
                        as='label'
                        htmlFor={modalId}
                        className='w-full'
                        emphasis='secondary'
                        size='lg'
                        variant='ghost'
                    >
                        {backLabel}
                    </Button>
                )}
            </div>
            {showOzonOauth && oauthModalPhase !== 'idle' ? (
                <Modal id={MARKETPLACE_OAUTH_MODAL_ID} showCloseButton={false} aria-label='Подключение через Ozon'>
                    {oauthModalPhase === 'loading' ? (
                        <div className='flex flex-col items-center gap-4 py-2 text-center'>
                            <p className='text-base'>Идёт подключение через Ozon…</p>
                            <p className='text-sm text-base-content/70'>
                                Завершите подключение во всплывающем окне. Если окно не открылось, разрешите всплывающие
                                окна в браузере.
                            </p>
                            <Button
                                type='button'
                                variant='ghost'
                                emphasis='secondary'
                                size='md'
                                onClick={handleCancelMarketplaceOauth}
                            >
                                Отмена
                            </Button>
                        </div>
                    ) : null}
                    {oauthModalPhase === 'success' ? (
                        <div className='flex flex-col items-center gap-4 py-2 text-center' role='status'>
                            <Icon name='tick-circle' size='xl' className='text-success' aria-hidden />
                            <p className='text-base font-medium'>{successMessage}</p>
                        </div>
                    ) : null}
                    {oauthModalPhase === 'error' ? (
                        <div className='flex flex-col items-center gap-4 py-2 text-center' role='alert'>
                            <Icon name='danger' size='xl' className='text-error' aria-hidden />
                            <p className='text-base text-error'>{oauthErrorMessage}</p>
                            <Button
                                type='button'
                                variant='ghost'
                                emphasis='secondary'
                                size='md'
                                onClick={handleCloseOauthResultModal}
                            >
                                Закрыть
                            </Button>
                        </div>
                    ) : null}
                </Modal>
            ) : null}
        </form>
    )
}
